/**
 * Credit Context
 *
 * Holds the user's credit balance in a single shared place so the header
 * widget, wallet page, dashboard, and checkout flows all stay in sync.
 * Call `refreshCredits()` after any operation that changes the balance
 * (purchase, promo redeem, subscription activation).
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getCreditBalance } from '../services/api';
import { useAuth } from './AuthContext';

const CreditContext = createContext(null);

export const useCredits = () => {
  const ctx = useContext(CreditContext);
  if (!ctx) {
    throw new Error('useCredits must be used within a CreditProvider');
  }
  return ctx;
};

export const CreditProvider = ({ children }) => {
  const { authenticated } = useAuth();
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refreshCredits = useCallback(async () => {
    if (!authenticated) {
      setBalance(null);
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getCreditBalance();
      setBalance(data);
      return data;
    } catch (err) {
      console.error('Failed to load credit balance:', err);
      setError('Failed to load');
      return null;
    } finally {
      setLoading(false);
    }
  }, [authenticated]);

  useEffect(() => {
    if (authenticated) {
      refreshCredits();
    } else {
      setBalance(null);
      setError(null);
    }
  }, [authenticated, refreshCredits]);

  useEffect(() => {
    if (!authenticated) return undefined;
    const onFocus = () => { refreshCredits(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [authenticated, refreshCredits]);

  const value = { balance, loading, error, refreshCredits };

  return (
    <CreditContext.Provider value={value}>
      {children}
    </CreditContext.Provider>
  );
};

export default CreditContext;
